import React from 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    alpha
} from '@mui/material';
import { CheckCircleFilled, CloseCircleFilled, InfoCircleFilled, WarningFilled, BulbFilled } from '@ant-design/icons';

import { ArticleBlock, ArticleSection } from 'types/blog';
import { inline } from './InlineText';
import ArticleCta from './ArticleCta';
import ArticleImage from './ArticleImage';
import SourceAccordion from './SourceAccordion';

// ==============================|| 記事本文の描画 ||============================== //
//
// 記事データのブロックを、ひとつずつ見た目に変換する。
// 見た目の定義はすべてこのファイルに閉じてあるため、デザインを変えるときに
// 記事データ(src/const/blog/articles/*.ts)を触る必要はない。
//
// 読みやすさのための決めごと:
//   - 本文は line-height 2.05、文字色は #374151。長文を読み続けても目が滑らないようにしている
//   - 見出しの直前だけ大きく余白を取り、見出しと本文の間は詰める(かたまりとして見せる)
//   - 表は横スクロールできる箱に入れる。スマホで表がはみ出して横スクロールが出ると
//     ページ全体が横に動いてしまうため

const primaryColor = '#2aa1af';
const bodyColor = '#374151';

/** 囲み(note)の見た目。tone ごとに色とアイコンを持たせる */
const NOTE_TONES = {
    info: { color: '#2563eb', icon: <InfoCircleFilled />, label: '補足' },
    warn: { color: '#dc2626', icon: <WarningFilled />, label: '注意' },
    good: { color: '#059669', icon: <BulbFilled />, label: 'ポイント' }
} as const;

const Paragraph = ({ text }: { text: string }) => (
    <Typography
        component="p"
        sx={{
            color: bodyColor,
            fontSize: { xs: '0.97rem', md: '1.02rem' },
            lineHeight: 2.05,
            mb: 3
        }}
    >
        {inline(text)}
    </Typography>
);

const Subheading = ({ text }: { text: string }) => (
    <Typography
        component="h3"
        sx={{
            mt: { xs: 5, md: 6 },
            mb: 2,
            pl: 2,
            borderLeft: `4px solid ${primaryColor}`,
            fontSize: { xs: '1.12rem', md: '1.28rem' },
            fontWeight: 800,
            lineHeight: 1.5,
            color: '#0f3d43'
        }}
    >
        {text}
    </Typography>
);

const BulletList = ({ items, ordered }: { items: string[]; ordered?: boolean }) => (
    <Box
        component={ordered ? 'ol' : 'ul'}
        sx={{
            m: 0,
            mb: 3,
            pl: 0,
            listStyle: 'none',
            counterReset: ordered ? 'article-list' : undefined
        }}
    >
        {items.map((item, index) => (
            <Box
                component="li"
                key={index}
                sx={{
                    position: 'relative',
                    pl: 4,
                    mb: 1.5,
                    color: bodyColor,
                    fontSize: { xs: '0.95rem', md: '1rem' },
                    lineHeight: 1.95,
                    // 番号付きは連番、記号なしは丸を、疑似要素で左端に置く。
                    // ブラウザ既定のマーカーだと、行が折り返したときに文字が
                    // マーカーの下に回り込んで読みにくくなるため自前で描いている
                    '&::before': ordered
                        ? {
                              counterIncrement: 'article-list',
                              content: 'counter(article-list)',
                              position: 'absolute',
                              left: 0,
                              top: '0.35em',
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              bgcolor: primaryColor,
                              color: '#fff',
                              fontSize: '0.72rem',
                              fontWeight: 800,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: 1
                          }
                        : {
                              content: '""',
                              position: 'absolute',
                              left: 6,
                              top: '0.78em',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: alpha(primaryColor, 0.75)
                          }
                }}
            >
                {inline(item)}
            </Box>
        ))}
    </Box>
);

const DataTable = ({ head, rows, caption }: { head: string[]; rows: string[][]; caption?: string }) => (
    <Box sx={{ mb: 4 }}>
        {caption && (
            <Typography component="p" sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#6b7280', mb: 1 }}>
                {caption}
            </Typography>
        )}
        <TableContainer
            sx={{
                border: `1px solid ${alpha(primaryColor, 0.22)}`,
                borderRadius: 2,
                overflowX: 'auto'
            }}
        >
            <Table size="small" sx={{ minWidth: 520 }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: alpha(primaryColor, 0.1) }}>
                        {head.map((cell, index) => (
                            <TableCell key={index} sx={{ fontWeight: 800, color: '#0f3d43', fontSize: '0.88rem', borderBottomColor: alpha(primaryColor, 0.22) }}>
                                {cell}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex} sx={{ '&:last-of-type td': { borderBottom: 0 } }}>
                            {row.map((cell, cellIndex) => (
                                <TableCell
                                    key={cellIndex}
                                    sx={{
                                        color: bodyColor,
                                        fontSize: '0.88rem',
                                        lineHeight: 1.8,
                                        verticalAlign: 'top',
                                        fontWeight: cellIndex === 0 ? 700 : 400,
                                        borderBottomColor: alpha(primaryColor, 0.14)
                                    }}
                                >
                                    {inline(cell)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

const Note = ({ tone = 'info', title, body }: { tone?: 'info' | 'warn' | 'good'; title?: string; body: string[] }) => {
    const { color, icon, label } = NOTE_TONES[tone];
    return (
        <Box
            sx={{
                my: 4,
                p: { xs: 2.5, md: 3 },
                borderRadius: 2,
                border: `1px solid ${alpha(color, 0.3)}`,
                bgcolor: alpha(color, 0.05)
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: title ? 1.5 : 1 }}>
                <Box sx={{ color, display: 'flex', fontSize: '1rem' }}>{icon}</Box>
                <Typography component="span" sx={{ color, fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.08em' }}>
                    {label}
                </Typography>
            </Stack>
            {title && (
                <Typography component="p" sx={{ fontWeight: 800, fontSize: { xs: '1rem', md: '1.06rem' }, color: '#111827', mb: 1.5, lineHeight: 1.6 }}>
                    {title}
                </Typography>
            )}
            {body.map((line, index) => (
                <Typography
                    key={index}
                    component="p"
                    sx={{ color: bodyColor, fontSize: { xs: '0.92rem', md: '0.96rem' }, lineHeight: 1.95, mb: index === body.length - 1 ? 0 : 1.25 }}
                >
                    {inline(line)}
                </Typography>
            ))}
        </Box>
    );
};

/**
 * 文例カード。
 * 読者がここだけを拾って書き写せるよう、本文とは明確に見た目を分けている。
 */
const ExampleCard = ({ label, title, body }: { label?: string; title: string; body: string[] }) => (
    <Box
        sx={{
            my: 4,
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${alpha(primaryColor, 0.28)}`
        }}
    >
        <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, bgcolor: alpha(primaryColor, 0.12) }}
        >
            <Typography
                component="span"
                sx={{ px: 1.25, py: 0.25, borderRadius: 1, bgcolor: primaryColor, color: '#fff', fontSize: '0.7rem', fontWeight: 800, whiteSpace: 'nowrap' }}
            >
                {label ?? '文例'}
            </Typography>
            <Typography component="p" sx={{ fontWeight: 800, fontSize: { xs: '0.92rem', md: '0.98rem' }, color: '#0f3d43', lineHeight: 1.5 }}>
                {title}
            </Typography>
        </Stack>
        <Box sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 2, md: 2.5 }, bgcolor: '#fff' }}>
            {body.map((line, index) => (
                <Typography
                    key={index}
                    component="p"
                    sx={{
                        color: bodyColor,
                        fontSize: { xs: '0.92rem', md: '0.96rem' },
                        lineHeight: 2,
                        mb: index === body.length - 1 ? 0 : 1.5
                    }}
                >
                    {inline(line)}
                </Typography>
            ))}
        </Box>
    </Box>
);

/**
 * つまずきやすい例と、正しい扱いを並べる。理由まで見せることで真似が利くようにしている。
 *
 * 見出しは記事側で差し替えられる(CompareBlock の badLabel / goodLabel)。
 * 対比しているものが文章とは限らないため。減算の記事では
 * 「公表したので減算は避けられたと考えた」のような判断を対比しており、
 * そこに「ありがちな書き方／こう書き直す」と出すと、書くものが何も無いのに
 * 書き直しを促す見出しになってしまう。
 */
const Compare = ({
    title,
    badLabel,
    bad,
    badReason,
    goodLabel,
    good,
    goodReason
}: {
    title?: string;
    badLabel?: string;
    bad: string;
    badReason: string;
    goodLabel?: string;
    good: string;
    goodReason: string;
}) => {
    const row = (
        variant: 'bad' | 'good',
        text: string,
        reason: string
    ) => {
        const isBad = variant === 'bad';
        const color = isBad ? '#dc2626' : '#059669';
        return (
            <Box sx={{ p: { xs: 2, md: 2.5 }, bgcolor: alpha(color, 0.045), borderTop: isBad ? 'none' : `1px dashed ${alpha('#111827', 0.12)}` }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ color, display: 'flex', fontSize: '0.95rem' }}>{isBad ? <CloseCircleFilled /> : <CheckCircleFilled />}</Box>
                    <Typography component="span" sx={{ color, fontWeight: 800, fontSize: '0.76rem', letterSpacing: '0.06em' }}>
                        {isBad ? badLabel ?? 'ありがちな対応' : goodLabel ?? 'こうしておく'}
                    </Typography>
                </Stack>
                <Typography
                    component="p"
                    sx={{ color: '#111827', fontWeight: 700, fontSize: { xs: '0.93rem', md: '0.98rem' }, lineHeight: 1.9, mb: 1 }}
                >
                    {inline(text)}
                </Typography>
                <Typography component="p" sx={{ color: '#6b7280', fontSize: { xs: '0.85rem', md: '0.88rem' }, lineHeight: 1.9 }}>
                    {inline(reason)}
                </Typography>
            </Box>
        );
    };

    return (
        <Box sx={{ my: 4, borderRadius: 2, overflow: 'hidden', border: `1px solid ${alpha('#111827', 0.12)}` }}>
            {title && (
                <Typography
                    component="p"
                    sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, bgcolor: '#f3f4f6', fontWeight: 800, fontSize: { xs: '0.92rem', md: '0.98rem' }, color: '#111827' }}
                >
                    {title}
                </Typography>
            )}
            {row('bad', bad, badReason)}
            {row('good', good, goodReason)}
        </Box>
    );
};

const Steps = ({ items }: { items: { title: string; body: string }[] }) => (
    <Box sx={{ my: 4 }}>
        {items.map((item, index) => (
            <Stack key={index} direction="row" spacing={2} sx={{ position: 'relative', pb: index === items.length - 1 ? 0 : 3 }}>
                {/* 番号と、次の番号へつながる縦線。最後の項目には線を引かない */}
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            bgcolor: primaryColor,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            fontSize: '0.85rem'
                        }}
                    >
                        {index + 1}
                    </Box>
                    {index !== items.length - 1 && (
                        <Box
                            aria-hidden="true"
                            sx={{ position: 'absolute', left: '50%', top: 34, bottom: -24, width: 2, bgcolor: alpha(primaryColor, 0.25), transform: 'translateX(-50%)' }}
                        />
                    )}
                </Box>
                <Box sx={{ pt: 0.4 }}>
                    <Typography component="p" sx={{ fontWeight: 800, fontSize: { xs: '1rem', md: '1.05rem' }, color: '#0f3d43', mb: 0.75, lineHeight: 1.6 }}>
                        {item.title}
                    </Typography>
                    <Typography component="p" sx={{ color: bodyColor, fontSize: { xs: '0.92rem', md: '0.96rem' }, lineHeight: 1.95 }}>
                        {inline(item.body)}
                    </Typography>
                </Box>
            </Stack>
        ))}
    </Box>
);

/**
 * よくある質問。
 * アコーディオンにはしていない。折りたたむとプリレンダリングしたHTMLの中で
 * 回答が隠れた状態になり、検索エンジンから見た本文量が減るため、常に開いた形で出す。
 */
const Faq = ({ items }: { items: { q: string; a: string }[] }) => (
    <Box sx={{ my: 2 }}>
        {items.map((item, index) => (
            <Box
                key={index}
                sx={{
                    mb: 2,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 2,
                    border: `1px solid ${alpha('#111827', 0.1)}`,
                    bgcolor: '#fff'
                }}
            >
                <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Box sx={{ color: primaryColor, fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.6, flexShrink: 0 }}>Q.</Box>
                    <Typography component="p" sx={{ fontWeight: 800, fontSize: { xs: '0.98rem', md: '1.04rem' }, color: '#111827', lineHeight: 1.7 }}>
                        {item.q}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5}>
                    <Box sx={{ color: '#9ca3af', fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.9, flexShrink: 0 }}>A.</Box>
                    <Typography component="p" sx={{ color: bodyColor, fontSize: { xs: '0.92rem', md: '0.96rem' }, lineHeight: 1.95 }}>
                        {inline(item.a)}
                    </Typography>
                </Stack>
            </Box>
        ))}
    </Box>
);

const renderBlock = (block: ArticleBlock, key: number) => {
    switch (block.type) {
        case 'p':
            return <Paragraph key={key} text={block.text} />;
        case 'h3':
            return <Subheading key={key} text={block.text} />;
        case 'list':
            return <BulletList key={key} items={block.items} ordered={block.ordered} />;
        case 'table':
            return <DataTable key={key} head={block.head} rows={block.rows} caption={block.caption} />;
        case 'note':
            return <Note key={key} tone={block.tone} title={block.title} body={block.body} />;
        case 'example':
            return <ExampleCard key={key} label={block.label} title={block.title} body={block.body} />;
        case 'compare':
            return <Compare key={key} {...block} />;
        case 'steps':
            return <Steps key={key} items={block.items} />;
        case 'cta':
            return <ArticleCta key={key} block={block} />;
        case 'faq':
            return <Faq key={key} items={block.items} />;
        case 'source':
            return <SourceAccordion key={key} title={block.title} items={block.items} />;
        case 'image':
            return <ArticleImage key={key} block={block} />;
        default:
            return null;
    }
};

// ==============================|| 章 ||============================== //
//
// id を h2 の親に振っているのは、目次から飛んだときに見出しが画面の一番上に
// 貼りついて読みにくくなるのを防ぐため(scroll-margin-top で少し余白を作っている)。

const ArticleBody = ({ sections }: { sections: ArticleSection[] }) => (
    <Box component="div">
        {sections.map((section) => (
            <Box
                key={section.id}
                component="section"
                id={section.id}
                // 目次から飛んだとき、固定ヘッダー(BlogHeader)の下に見出しが潜らないよう、
                // ヘッダーの高さぶん余分に余白を取る
                sx={{ mt: { xs: 7, md: 9 }, scrollMarginTop: { xs: 72, md: 80 } }}
            >
                <Typography
                    component="h2"
                    sx={{
                        position: 'relative',
                        mb: 3,
                        pb: 1.5,
                        fontSize: { xs: '1.35rem', md: '1.7rem' },
                        fontWeight: 900,
                        lineHeight: 1.45,
                        color: '#0f3d43',
                        borderBottom: `2px solid ${alpha(primaryColor, 0.25)}`,
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            bottom: -2,
                            width: 72,
                            height: 2,
                            bgcolor: primaryColor
                        }
                    }}
                >
                    {section.heading}
                </Typography>
                {section.blocks.map((block, index) => renderBlock(block, index))}
            </Box>
        ))}
    </Box>
);

export default ArticleBody;
